import React from 'react';
import { ApiOutlined, CopyOutlined } from '@ant-design/icons';
import { useAtom } from 'jotai';
import copy from 'copy-to-clipboard';
import { Tooltip, message } from 'antd';
import {
  ActionBar,
  StyledButton,
  StyledEllipsis,
  StyledEllipsisTooltip,
} from '@components/dimension-panel-hz/dimension-table/styled';
import { EActionKey, networkRequestUrlDimensions } from './utils/constant';
import { useDrillDownToSSP } from './utils/useDrillDownToSSP';
import { traceIdInfoAtom } from './utils/atom';
import { useSelector } from 'react-redux';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';
import { useAddGlobalFilter } from './utils/useAddGlobalFilter';
import { DrillDownToEventScope } from '../common/DrillDownToEventScope';
import { useExistentDimension } from '@src/utils/appInsights';

// rowData will have multi dimensions
export const MultiDimenCell = ({ cellData, column, rowData, actionKeys }) => {
  let dimensions = [column.key];
  if (column.key === 'network_api_calls') {
    dimensions = Object.values(networkRequestUrlDimensions);
  }
  dimensions = useExistentDimension(dimensions);
  const { usecaseKey } = useSelector(activeDashboardSelector);
  const enableDrillDownIcon = !usecaseKey;
  const [traceIdInfo, setTraceIdInfo] = useAtom(traceIdInfoAtom);
  const drillDown = useDrillDownToSSP(dimensions, rowData);
  const addGlobalFilter = useAddGlobalFilter(dimensions, rowData);
  const handleClick = () => {
    if (!rowData.disableClickAction) {
      addGlobalFilter();
    }
  };

  const handleActions = (value) => {
    if (value === EActionKey.drillDown) {
      return () => {
        drillDown();
      };
    }
    if (value === EActionKey.copy) {
      return () => {
        if (copy(cellData, { format: 'text/plain' })) {
          message.info('Copied to clipboard');
        }
      };
    }
    if (value === EActionKey.traceId) {
      return () => {
        setTraceIdInfo({
          ...traceIdInfo,
          open: true,
          info: {
            [networkRequestUrlDimensions.host]: rowData[networkRequestUrlDimensions.host],
            [networkRequestUrlDimensions.path]: rowData[networkRequestUrlDimensions.path],
            [networkRequestUrlDimensions.query]: rowData[networkRequestUrlDimensions.query],
          },
        });
      };
    }
    return () => null;
  };

  const resActionKeys =
    enableDrillDownIcon && !rowData.disableClickAction
      ? [EActionKey.drillDown, ...actionKeys]
      : actionKeys;
  const actions = resActionKeys.map((k) => actionsMap[k]);
  const styles = rowData.disableClickAction ? { cursor: 'default' } : {};

  return (
    <>
      <StyledEllipsis onClick={handleClick} style={styles}>
        <StyledEllipsisTooltip text={cellData} />
      </StyledEllipsis>
      {rowData.totalRow ? null : (
        <Actions actions={actions} handleActions={handleActions} dimension={dimensions[0]} />
      )}
    </>
  );
};

const actionsMap = {
  [EActionKey.copy]: { label: <CopyOutlined />, value: EActionKey.copy },
  [EActionKey.drillDown]: { label: <div />, value: EActionKey.drillDown },
  [EActionKey.traceId]: { label: <ApiOutlined />, value: EActionKey.traceId },
};

const Actions = ({ actions, handleActions, dimension }) => {
  return (
    <ActionBar className="action-bar">
      {actions.map((action) => {
        if (action.value === EActionKey.drillDown) {
          return (
            <DrillDownToEventScope
              dimension={dimension}
              handleAction={handleActions(action.value)}
              key={action.value}
            />
          );
        }
        if (action.value === EActionKey.traceId) {
          return (
            <Tooltip title="Show Trace ID" key={action.value}>
              <StyledButton
                key={action.value}
                type="text"
                icon={action.label}
                onClick={handleActions(action.value)}
                $isAppModule
              />
            </Tooltip>
          );
        }
        return (
          <StyledButton
            key={action.value}
            type="text"
            icon={action.label}
            onClick={handleActions(action.value)}
            $isAppModule
          />
        );
      })}
    </ActionBar>
  );
};
